import * as React from 'react';
import * as moment from 'moment';
import { IWatrRequest } from '@nkr/models/index';
import { IWatrTrip } from '@nkr/models/watr-trip';
import { filterAddress } from '../utils/trip.utils';
import { getDefaultMileageRate } from '../constants/reimbursement';
import { ExpenseType } from '../constants/expense-types';
import { getMileageRateForDate } from '../../year-rates';
import { ILineItem } from '../types/line-item.types';
import { sumMileageLineItemAmount, deriveMileageRateFromLineItems } from '../utils/expense.utils';
import { getTripPhaseName } from '../constants/trip-phases';
import { ITripGroup } from '../types/trip.types';
import { formatMoneyDisplay } from '../../common/money-input';

interface MileageReviewPanelProps {
    data: IWatrRequest;
    lineItems: ILineItem[];
    trip?: IWatrTrip | null;
    tripGroups?: ITripGroup[];
}

export const MileageReviewPanel = (props: MileageReviewPanelProps) => {
    const tripIdToTripMap = new Map<number, IWatrTrip>();
    if (props.tripGroups) {
        props.tripGroups.forEach(group => {
            tripIdToTripMap.set(group.trip.id, group.trip);
        });
    }

    if (props.trip) {
        tripIdToTripMap.set(props.trip.id, props.trip);
    }

    if (props.data?.requestTravels) {
        props.data.requestTravels.forEach((tr: any) => {
            (tr.trips || []).forEach((t: IWatrTrip) => {
                tripIdToTripMap.set(t.id, t);
            });
        });
    }

    const mileageLineItems = (props.lineItems || [])
        .filter(li => li.expenseType === ExpenseType.MILEAGE)
        .sort((a, b) => {
            const dateA = a.expenseDate ? moment(a.expenseDate).valueOf() : (a.createdDateTime ? moment(a.createdDateTime).valueOf() : 0);
            const dateB = b.expenseDate ? moment(b.expenseDate).valueOf() : (b.createdDateTime ? moment(b.createdDateTime).valueOf() : 0);
            return dateA - dateB;
        });

    const fallbackTrip = props.trip || (tripIdToTripMap.size > 0 ? Array.from(tripIdToTripMap.values())[0] : null);
    const storedRate = deriveMileageRateFromLineItems(mileageLineItems);
    const displayRate = storedRate > 0
        ? storedRate
        : (fallbackTrip?.reimbursementRate
            ?? getMileageRateForDate(fallbackTrip?.tripDate ? new Date(fallbackTrip.tripDate as any) : undefined)
            ?? getDefaultMileageRate());

    const totalOneWay = mileageLineItems.reduce((sum, li) => sum + (li.numberOfNights || 0), 0);
    const totalRoundTrip = totalOneWay * 2;
    const totalReimbursement = sumMileageLineItemAmount(mileageLineItems);
    const mileageCap = props.data?.limit?.mileage ?? 0;
    const hasMultipleEntries = mileageLineItems.length > 1;

    const mapsOrigin = props.data
        ? encodeURI(
            filterAddress(props.data.addressLine1) + ', ' + props.data.city + ', ' + props.data.stateAbbr + ' ' + props.data.postalCode
        )
        : '';
    const mapsDest = props.data
        ? `${props.data.centerLatitude},${props.data.centerLongitude}`
        : '';
    const mapsUrl = mapsOrigin && mapsDest
        ? `https://www.google.com/maps/dir/?api=1&origin=${mapsOrigin}&destination=${mapsDest}`
        : '';

    const resolveLineContext = (lineItem: ILineItem) => {
        const lineItemTrip = (lineItem.tripId && tripIdToTripMap.get(lineItem.tripId)) || fallbackTrip;
        const phaseName = lineItemTrip ? getTripPhaseName(lineItemTrip) : (lineItem.description || 'Mileage');
        const dateLabel = lineItem.expenseDate
            ? moment(lineItem.expenseDate).format('MMM D, YYYY')
            : (lineItem.createdDateTime
                ? moment(lineItem.createdDateTime).format('MMM D, YYYY')
                : '—');

        return { phaseName, dateLabel };
    };

    return (
        <div style={shellStyle}>
            <div style={cardStyle}>
                <div style={headerStyle}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>
                        No receipt required
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                        Self-Driving Mileage
                    </div>
                    <div style={{ fontSize: 14, color: '#64748b', marginTop: 8, lineHeight: 1.45, maxWidth: 420 }}>
                        Confirm the route looks right, then Approve — same as a receipt.
                    </div>
                </div>

                {mileageLineItems.length === 0 ? (
                    <div style={{ padding: '28px 8px', textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                        No self-driving mileage for this trip.
                    </div>
                ) : (
                    <>
                        {!hasMultipleEntries && (() => {
                            const { phaseName, dateLabel } = resolveLineContext(mileageLineItems[0]);

                            return (
                                <div style={contextRowStyle}>
                                    <span style={phasePillStyle}>{phaseName}</span>
                                    <span style={{ fontSize: 13, color: '#64748b' }}>{dateLabel}</span>
                                </div>
                            );
                        })()}

                        <div style={statsRowStyle}>
                            <div style={statStyle}>
                                <div style={statLabelStyle}>One-way</div>
                                <div style={statValueStyle}>{totalOneWay.toLocaleString('en-US')}</div>
                                <div style={statHintStyle}>miles</div>
                            </div>
                            <div style={statDividerStyle} />
                            <div style={statStyle}>
                                <div style={statLabelStyle}>Round trip</div>
                                <div style={statValueStyle}>{totalRoundTrip.toLocaleString('en-US')}</div>
                                <div style={statHintStyle}>miles</div>
                            </div>
                            <div style={statDividerStyle} />
                            <div style={statStyle}>
                                <div style={statLabelStyle}>Pays</div>
                                <div style={{ ...statValueStyle, color: '#059669' }}>{formatMoneyDisplay(totalReimbursement)}</div>
                                <div style={statHintStyle}>@ ${displayRate.toFixed(2)}/mi</div>
                            </div>
                        </div>

                        {hasMultipleEntries && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                                {mileageLineItems.map((lineItem, index) => {
                                    const { phaseName, dateLabel } = resolveLineContext(lineItem);
                                    const oneWayMiles = lineItem.numberOfNights || 0;
                                    const roundTripMiles = oneWayMiles * 2;
                                    const reimbursement = lineItem.amount || 0;

                                    return (
                                        <div key={lineItem.id || index} style={tripCardStyle}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                                                <span style={phasePillStyle}>{phaseName}</span>
                                                <span style={{ fontSize: 13, color: '#64748b' }}>{dateLabel}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, color: '#334155' }}>
                                                <span>
                                                    {oneWayMiles.toLocaleString('en-US')} one-way
                                                    <span style={{ color: '#94a3b8' }}> · </span>
                                                    {roundTripMiles.toLocaleString('en-US')} round trip
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#059669' }}>
                                                    {formatMoneyDisplay(reimbursement)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {mapsUrl && (
                            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={verifyBtnStyle}>
                                <span style={{ fontSize: 16, lineHeight: 1 }}>↗</span>
                                Open route in Maps to verify miles
                            </a>
                        )}

                        <div style={footerMetaStyle}>
                            Cap {mileageCap.toLocaleString('en-us')} miles · ${displayRate.toFixed(2)} per mile
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const shellStyle: React.CSSProperties = {
    height: '100%',
    overflow: 'auto',
    background: 'linear-gradient(165deg, #f1f5f9 0%, #e2e8f0 55%, #f8fafc 100%)',
    padding: '24px 20px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const cardStyle: React.CSSProperties = {
    background: '#ffffff',
    borderRadius: 16,
    border: '1px solid rgba(148, 163, 184, 0.35)',
    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
    width: '100%',
    maxWidth: 520,
    padding: '28px 28px 24px',
    boxSizing: 'border-box'
};

const headerStyle: React.CSSProperties = {
    marginBottom: 18,
    textAlign: 'center'
};

const contextRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 14
};

const statsRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: 8,
    padding: '16px 12px',
    background: '#f8fafc',
    borderRadius: 12,
    border: '1px solid #e2e8f0'
};

const statStyle: React.CSSProperties = {
    flex: 1,
    textAlign: 'center',
    minWidth: 0
};

const statLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
};

const statValueStyle: React.CSSProperties = {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.03em',
    marginTop: 4,
    lineHeight: 1.1
};

const statHintStyle: React.CSSProperties = {
    fontSize: 12,
    color: '#64748b',
    marginTop: 4
};

const statDividerStyle: React.CSSProperties = {
    width: 1,
    background: '#e2e8f0',
    alignSelf: 'stretch',
    flexShrink: 0
};

const tripCardStyle: React.CSSProperties = {
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    background: '#ffffff'
};

const phasePillStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 600,
    color: '#1d4ed8',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 999,
    padding: '3px 10px'
};

const verifyBtnStyle: React.CSSProperties = {
    marginTop: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '14px 16px',
    borderRadius: 12,
    background: '#0f172a',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 600,
    textDecoration: 'none',
    boxSizing: 'border-box'
};

const footerMetaStyle: React.CSSProperties = {
    marginTop: 14,
    textAlign: 'center',
    fontSize: 12,
    color: '#94a3b8'
};

export default MileageReviewPanel;
